import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { capitalize } from "lodash";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  Divider,
  Tooltip,
  Stack,
  Box,
  useMediaQuery,
  Pagination,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Close as CloseIcon,
  AutoFixHigh as AutoFixHighIcon,
  ClearAll as ClearAllIcon,
  MyLocation as MyLocationIcon,
  TableRows as TableRowsIcon,
} from "@mui/icons-material";
import PreviewPDF from "./PreviewPDF";
import PreviewForm from "./PreviewForm";
import {
  findWidgetsForClickedWidget,
  findWidgetsForPreviewField,
  isPreviewRowField,
} from "./previewFieldSelection";
import {
  isPreviewTextWidget,
  movePreviewFieldAfter,
  swapPreviewFieldOrders,
} from "./previewFieldOrder";

const snapshotFields = (currentProcessingFile) => {
  const fields = currentProcessingFile?.document?.tempFields?.formFields ?? [];
  return fields;
};

const getPreviewFieldKey = (field) =>
  String(field?.field_id ?? field?.name ?? "");

const clearEditorSelection = (currentProcessingFile) => ({
  ...currentProcessingFile,
  document: {
    ...currentProcessingFile?.document,
    tempFields: {
      ...currentProcessingFile?.document?.tempFields,
      selectedFormField: null,
      selectedExtraFields: [],
      selectedPdfTextItem: null,
      selectedExtraPdfTextItems: [],
    },
  },
});

const buildPreviewFile = (
  currentProcessingFile,
  overrideFields,
  selectedPreviewFields = [],
) => ({
  ...clearEditorSelection(currentProcessingFile),
  document: {
    ...currentProcessingFile?.document,
    tempFields: {
      ...currentProcessingFile?.document?.tempFields,
      formFields: overrideFields,
      selectedFormField: selectedPreviewFields[0] ?? null,
      selectedExtraFields: selectedPreviewFields,
      selectedPdfTextItem: null,
      selectedExtraPdfTextItems: [],
    },
  },
});

const PreviewFormDialog = ({
  open,
  setOpen,
  base64File,
  pdfInput,
  currentProcessingFile,
  setCurrentProcessingFile,
  pageNumber,
  displayRotation,
  onDocumentLoadSuccess,
  onPageLoadSuccess,
  titleMetadata,
  fields,
  numPages,
  handlePageChange,
}) => {
  const isSm = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [previewFields, setPreviewFields] = useState([]);
  const [enableSorting, setEnableSorting] = useState(true);
  const [locateFields, setLocateFields] = useState(false);
  const [locatedFields, setLocatedFields] = useState([]);
  const [hoveredFields, setHoveredFields] = useState([]);
  const [rowSelectedFields, setRowSelectedFields] = useState([]);
  const [documentSwapSource, setDocumentSwapSource] = useState(null);
  const [previewFieldScrollRequest, setPreviewFieldScrollRequest] = useState({
    fieldId: "",
    requestId: 0,
  });

  const baseViewPort = useMemo(() => {
    const currentDocument =
      currentProcessingFile?.document?.tempFields?.currentDocument;

    if (currentDocument?.viewport?.transform) {
      return currentDocument.viewport;
    }

    const width = currentDocument?.actualWidth;
    const height = currentDocument?.actualHeight;
    if (!width || !height) return null;

    return {
      width,
      height,
      rotation: 0,
      transform: [1, 0, 0, -1, 0, height],
    };
  }, [currentProcessingFile]);

  useEffect(() => {
    if (open) {
      setCurrentProcessingFile((prev) => clearEditorSelection(prev));
      setPreviewFields(snapshotFields(currentProcessingFile));
      setLocateFields(false);
      setLocatedFields([]);
      setHoveredFields([]);
      setRowSelectedFields([]);
      setDocumentSwapSource(null);
      setPreviewFieldScrollRequest({ fieldId: "", requestId: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const previewProcessingFile = buildPreviewFile(
    currentProcessingFile,
    previewFields,
    locatedFields,
  );

  const setPreviewProcessingFile = useCallback(
    (updater) => {
      setPreviewFields((prevFields) => {
        const next =
          typeof updater === "function"
            ? updater(
                buildPreviewFile(
                  currentProcessingFile,
                  prevFields,
                  locatedFields,
                ),
              )
            : updater;
        return next?.document?.tempFields?.formFields ?? prevFields;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [currentProcessingFile, locatedFields],
  );

  const handleLocateField = useCallback(
    (field) => {
      const matchingWidgets = findWidgetsForPreviewField(previewFields, field);

      setLocatedFields(matchingWidgets);

      const targetPage = Number(
        matchingWidgets[0]?.page_num ?? matchingWidgets[0]?.page,
      );
      if (Number.isFinite(targetPage) && targetPage !== pageNumber) {
        handlePageChange(null, targetPage);
      }
    },
    [handlePageChange, pageNumber, previewFields],
  );

  const handleLocateWidget = useCallback(
    (widget, event) => {
      const isSwapClick = Boolean(event?.ctrlKey || event?.metaKey);
      const isSequentialClick = Boolean(event?.altKey);
      const isModifierClick = isSwapClick || isSequentialClick;

      if (
        isModifierClick &&
        isPreviewTextWidget(documentSwapSource) &&
        isPreviewTextWidget(widget) &&
        documentSwapSource?.name !== widget?.name
      ) {
        const updateOrders = (formFields = []) =>
          isSequentialClick
            ? movePreviewFieldAfter(formFields, documentSwapSource, widget)
            : swapPreviewFieldOrders(formFields, documentSwapSource, widget);

        setPreviewFields(updateOrders);
        setCurrentProcessingFile((prevFile) => ({
          ...prevFile,
          document: {
            ...prevFile.document,
            tempFields: {
              ...prevFile.document.tempFields,
              formFields: updateOrders(prevFile.document.tempFields.formFields),
            },
          },
        }));
        setDocumentSwapSource(null);
      } else if (!isModifierClick) {
        setDocumentSwapSource(isPreviewTextWidget(widget) ? widget : null);
      }

      setLocatedFields(findWidgetsForClickedWidget(previewFields, widget));
      setPreviewFieldScrollRequest((request) => ({
        fieldId: widget?.field_id || "",
        requestId: request.requestId + 1,
      }));
    },
    [documentSwapSource, previewFields, setCurrentProcessingFile],
  );

  const handleHoverField = useCallback(
    (field) => {
      setHoveredFields(
        field ? findWidgetsForPreviewField(previewFields, field) : [],
      );
    },
    [previewFields],
  );

  const handleToggleRowField = useCallback((field) => {
    if (!isPreviewRowField(field)) return;

    const fieldKey = getPreviewFieldKey(field);
    if (!fieldKey) return;

    setRowSelectedFields((selectedFields) =>
      selectedFields.some(
        (selectedField) => getPreviewFieldKey(selectedField) === fieldKey,
      )
        ? selectedFields.filter(
            (selectedField) => getPreviewFieldKey(selectedField) !== fieldKey,
          )
        : [...selectedFields, field],
    );
  }, []);

  const handleGroupIntoRow = useCallback(() => {
    if (rowSelectedFields.length < 2) return;

    const selectedWidgets = [];
    const selectedWidgetNames = new Set();

    rowSelectedFields.forEach((field) => {
      findWidgetsForPreviewField(previewFields, field).forEach((widget) => {
        if (!selectedWidgetNames.has(widget?.name)) {
          selectedWidgetNames.add(widget.name);
          selectedWidgets.push(widget);
        }
      });
    });

    if (selectedWidgets.length < 2) return;

    const rowPrefix = Date.now();
    const rowByWidgetName = new Map(
      selectedWidgets.map((widget, index) => [
        widget.name,
        `${rowPrefix}_${index + 1}`,
      ]),
    );
    const groupFields = (formFields = []) =>
      formFields.map((field) =>
        rowByWidgetName.has(field?.name)
          ? { ...field, row: rowByWidgetName.get(field.name) }
          : field,
      );

    setPreviewFields(groupFields);
    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: groupFields(prevFile.document.tempFields.formFields),
        },
      },
    }));
    setRowSelectedFields([]);
  }, [previewFields, rowSelectedFields, setCurrentProcessingFile]);

  const fillDemoRef = useRef(null);
  const clearRef = useRef(null);

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { height: "90vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Typography fontWeight={600}>Form Preview</Typography>

        {/* Preview controls */}
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={enableSorting}
                onChange={(event) => setEnableSorting(event.target.checked)}
              />
            }
            label={enableSorting ? "Disable Sorting" : "Enable Sorting"}
          />
          <Tooltip title="Click either a form field or a document widget to locate every linked instance">
            <Button
              size="small"
              variant={locateFields ? "contained" : "outlined"}
              color="secondary"
              startIcon={<MyLocationIcon />}
              onClick={() => {
                setLocateFields((enabled) => !enabled);
                setLocatedFields([]);
                setHoveredFields([]);
                setDocumentSwapSource(null);
              }}
              aria-pressed={locateFields}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              {locateFields ? "Locating Fields" : "Locate Fields"}
            </Button>
          </Tooltip>
          <Tooltip
            title={
              rowSelectedFields.length < 2
                ? "Ctrl/Cmd-click at least two form fields to select them"
                : `Group ${rowSelectedFields.length} selected fields into one row`
            }
          >
            <span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<TableRowsIcon />}
                disabled={rowSelectedFields.length < 2}
                onClick={handleGroupIntoRow}
                sx={{
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                }}
              >
                Group into Row
                {rowSelectedFields.length > 0
                  ? ` (${rowSelectedFields.length})`
                  : ""}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Populate all fields with representative sample data">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<AutoFixHighIcon />}
              onClick={() => fillDemoRef.current?.()}
              sx={{ textTransform: "none", borderRadius: 2, fontWeight: 600 }}
            >
              Fill with Demo Data
            </Button>
          </Tooltip>

          <Tooltip title="Reset all fields to empty">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<ClearAllIcon />}
              onClick={() => clearRef.current?.()}
              sx={{ textTransform: "none", borderRadius: 2 }}
            >
              Clear
            </Button>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          <IconButton onClick={() => setOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          flex: 1,
          overflow: "auto",
          backgroundColor: "#f5f5f5",
          px: 0,
          py: 0,
        }}
      >
        <Grid container sx={{ overflow: "hidden", height: "100%" }}>
          <Grid item xs={12} sm={6} sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: 1,
              }}
            >
              {numPages > 1 && (
                <Pagination
                  count={numPages}
                  page={pageNumber}
                  onChange={handlePageChange}
                  size="small"
                />
              )}
            </Box>
            <Box
              sx={{
                overflow: "auto",
                width: "100%",
                height: "calc(100% - 36px)",
              }}
            >
              <PreviewPDF
                base64File={base64File}
                pdfInput={pdfInput}
                currentProcessingFile={previewProcessingFile}
                setCurrentProcessingFile={setPreviewProcessingFile}
                pageNumber={pageNumber}
                displayRotation={displayRotation}
                onDocumentLoadSuccess={onDocumentLoadSuccess}
                onPageLoadSuccess={onPageLoadSuccess}
                baseViewPort={baseViewPort}
                formFields={fields}
                locateFields={locateFields}
                onLocateWidget={handleLocateWidget}
                hoveredFields={hoveredFields}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ height: "100%", minWidth: 0 }}>
            <Box
              sx={{
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: "12px 0px !important",
                mb: "4px",
              }}
            >
              <Typography
                color="primary"
                mx={1}
                align="center"
                variant={isSm ? "h6" : "h5"}
              >
                {titleMetadata?.title || "N/A"}
              </Typography>
              <Typography
                color="primary"
                variant="h6"
                sx={{ fontSize: "14px !important" }}
              >
                {titleMetadata?.pageNumber || 0} /{" "}
                {titleMetadata?.numPages || 0}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  mb: 0.5,
                  mt: 0,
                  fontSize: "14px !important",
                }}
                textAlign="center"
              >
                {titleMetadata?.municipality
                  ? `${capitalize(titleMetadata?.municipality)} - ${capitalize(
                      titleMetadata?.county,
                    )} County`
                  : `${capitalize(titleMetadata?.county)} County`}
              </Typography>
            </Box>
            <PreviewForm
              currentProcessingFile={previewProcessingFile}
              setCurrentProcessingFile={setPreviewProcessingFile}
              fillDemoRef={fillDemoRef}
              clearRef={clearRef}
              fields={fields}
              setDocCurrentProcessingFile={setCurrentProcessingFile}
              enableSorting={enableSorting}
              locateFields={locateFields}
              locatedField={locatedFields[0]}
              onLocateField={handleLocateField}
              onHoverField={handleHoverField}
              rowSelectedFields={rowSelectedFields}
              onToggleRowField={handleToggleRowField}
              scrollToFieldRequest={previewFieldScrollRequest}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={() => setOpen(false)}
          variant="contained"
          sx={{ textTransform: "none", borderRadius: 2 }}
        >
          Close Preview
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewFormDialog;
